// DEPRECATED: Use coach-dashboard.tsx. This file is kept for reference only.
// trainer-dashboard.tsx is a dev-only legacy surface — it uses profile.gym_id (gyms table FK)
// instead of organization_memberships for gym member resolution (audit risk C3/C5).
// The canonical coach surface is app/coach-dashboard.tsx, provisioned via lib/orgProvisioning.ts.
// Do NOT route production users here. See docs/DUEL_COACH_TIER_MODEL.md for architecture.

import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView,
  TextInput, TouchableOpacity, Alert, Platform, Share,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import PaywallGate from '@/components/PaywallGate';
import SkeletonRow from '@/components/SkeletonRow';
import { supabase } from '@/lib/supabase';
import {
  listMyClientLinks,
  listPendingClientRequests,
  acceptClientRequest,
  rejectClientRequest,
  requestTrainerClientLinkByUsername,
  getMyTrainerProfile,
  createTrainerProfile,
  type TrainerClientLink,
} from '@/lib/trainers';
import { UserPlus, Calendar, ChevronRight, Clock, Search, Link2, CheckCircle, XCircle } from 'lucide-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ── Types ─────────────────────────────────────────────────────

interface ClientRow {
  linkId: string;
  clientProfileId: string;
  displayName: string;
  handle: string | null;
}

interface CoachInsightsData {
  activeClients: number;
  totalClients: number;
  activity7d: number | null;
  activity30d: number | null;
}

// ── Coach Insights Section ────────────────────────────────────

function CoachInsights({ data }: { data: CoachInsightsData | null }) {
  const { theme } = useTheme();
  const c = theme.colors;

  if (!data) {
    return (
      <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[ci.value, { color: c.textSecondary }]}>Coach data unavailable.</Text>
      </View>
    );
  }

  return (
    <View style={ci.wrapper}>
      <Text style={[ci.sectionHeader, { color: c.textTertiary }]}>Coach Overview</Text>

      {/* Client Activity */}
      <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[ci.cardTitle, { color: c.text }]}>Client Activity</Text>
        <View style={ci.row}>
          <Text style={[ci.label, { color: c.textSecondary }]}>Last 7 days</Text>
          <Text style={[ci.rowValue, { color: c.text }]}>
            {data.activity7d != null ? `${data.activity7d} workouts` : '—'}
          </Text>
        </View>
        <View style={ci.row}>
          <Text style={[ci.label, { color: c.textSecondary }]}>Last 30 days</Text>
          <Text style={[ci.rowValue, { color: c.text }]}>
            {data.activity30d != null ? `${data.activity30d} workouts` : '—'}
          </Text>
        </View>
      </View>
    </View>
  );
}

// ── Client List ───────────────────────────────────────────────

function ClientList({
  clients,
  search,
  onSearch,
}: {
  clients: ClientRow[];
  search: string;
  onSearch: (q: string) => void;
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();

  const filtered = clients.filter((cl) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      cl.displayName.toLowerCase().includes(q) ||
      (cl.handle ?? '').toLowerCase().includes(q)
    );
  });

  return (
    <View style={cl.wrapper}>
      <Text style={[ci.sectionHeader, { color: c.textTertiary }]}>Clients</Text>
      <TextInput
        style={[cl.searchInput, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
        placeholder="Search clients…"
        placeholderTextColor={c.textTertiary}
        value={search}
        onChangeText={onSearch}
        autoCapitalize="none"
        autoCorrect={false}
      />
      {filtered.length === 0 ? (
        <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[ci.value, { color: c.textSecondary }]}>
            {clients.length === 0 ? 'No active clients.' : 'No clients match search.'}
          </Text>
        </View>
      ) : (
        filtered.map((client) => (
          <TouchableOpacity
            key={client.linkId}
            style={[cl.clientCard, { backgroundColor: c.surface, borderColor: c.border }]}
            activeOpacity={0.7}
            onPress={() =>
              router.push({
                pathname: '/(dev)/trainer-client-detail' as any,
                params: { clientId: client.clientProfileId },
              })
            }
          >
            <View>
              <Text style={[cl.clientName, { color: c.text }]}>
                {client.displayName || 'Client'}
              </Text>
              {client.handle ? (
                <Text style={[cl.clientHandle, { color: c.textSecondary }]}>
                  @{client.handle}
                </Text>
              ) : null}
            </View>
            <Text style={[cl.chevron, { color: c.textTertiary }]}>›</Text>
          </TouchableOpacity>
        ))
      )}
    </View>
  );
}

// ── Schedule Types ─────────────────────────────────────────────

type RecurType = 'none' | 'weekly' | 'biweekly';
type SessionType = 'gym' | 'intro' | 'assessment' | 'checkin';

const SESSION_TYPES: { key: SessionType; label: string }[] = [
  { key: 'gym', label: 'Gym Session' },
  { key: 'intro', label: 'First Meeting' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'checkin', label: 'Check-in' },
];

interface ScheduleEntry {
  id: string;
  clientName: string;
  clientId: string;
  time: string; // HH:MM or free text like "9:00 AM"
  notes: string;
  date: string; // YYYY-MM-DD
  recur?: RecurType;
  recurGroupId?: string; // shared ID for all instances of a recurring entry
  sessionType?: SessionType;
}

const SCHEDULE_STORAGE_KEY = 'duel:coach_schedule';

async function loadSchedule(): Promise<ScheduleEntry[]> {
  try {
    const raw = await AsyncStorage.getItem(SCHEDULE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) { console.warn('Failed to load cached schedule:', e); return []; }
}

async function saveSchedule(entries: ScheduleEntry[]): Promise<void> {
  await AsyncStorage.setItem(SCHEDULE_STORAGE_KEY, JSON.stringify(entries));
}

// ── Today's Schedule Section ──────────────────────────────────

function TodaySchedule({
  clients,
  onViewFull,
}: {
  clients: ClientRow[];
  onViewFull: () => void;
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const [todayEntries, setTodayEntries] = useState<ScheduleEntry[]>([]);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadSchedule().then((all) => {
      setTodayEntries(
        all.filter((e) => e.date === today).sort((a, b) => a.time.localeCompare(b.time))
      );
    });
  }, [today]);

  return (
    <View style={sched.wrapper}>
      <View style={sched.headerRow}>
        <Calendar size={16} color={c.accent} />
        <Text style={[ci.sectionHeader, { color: c.textTertiary, marginBottom: 0 }]}>
          Today&apos;s Schedule
        </Text>
        <View style={{ flex: 1 }} />
        <TouchableOpacity onPress={onViewFull} style={sched.viewAllBtn}>
          <Text style={[sched.viewAllText, { color: c.accent }]}>Full Calendar</Text>
          <ChevronRight size={14} color={c.accent} />
        </TouchableOpacity>
      </View>

      {todayEntries.length === 0 ? (
        <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[ci.value, { color: c.textSecondary }]}>No sessions scheduled today.</Text>
        </View>
      ) : (
        todayEntries.map((entry) => (
          <TouchableOpacity
            key={entry.id}
            style={[sched.entryCard, { backgroundColor: c.surface, borderColor: c.border }]}
            activeOpacity={0.7}
            onPress={() => {
              if (entry.clientId) {
                router.push({
                  pathname: '/(dev)/trainer-client-detail' as any,
                  params: { clientId: entry.clientId },
                });
              }
            }}
          >
            <View style={sched.entryTime}>
              <Clock size={14} color={c.textTertiary} />
              <Text style={[sched.timeText, { color: c.text }]}>{entry.time}</Text>
            </View>
            <View style={sched.entryInfo}>
              <Text style={[sched.clientNameText, { color: c.text }]}>{entry.clientName}</Text>
              {entry.notes ? (
                <Text style={[sched.notesText, { color: c.textSecondary }]} numberOfLines={1}>
                  {entry.notes}
                </Text>
              ) : null}
            </View>
            <ChevronRight size={16} color={c.textTertiary} />
          </TouchableOpacity>
        ))
      )}
    </View>
  );
}

// ── Add Client Section ────────────────────────────────────────

function AddClientSection({
  onClientAdded,
  gymId,
}: {
  onClientAdded: () => void;
  gymId: string | null;
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [expanded, setExpanded] = useState(false);
  const [lookupUsername, setLookupUsername] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [gymMembers, setGymMembers] = useState<{ id: string; handle: string; display_name: string }[]>([]);
  const [gymSearch, setGymSearch] = useState('');
  const [loadingGym, setLoadingGym] = useState(false);

  const loadGymMembers = useCallback(async () => {
    if (!gymId) return;
    setLoadingGym(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('id, handle, display_name')
        .eq('gym_id', gymId)
        .order('handle', { ascending: true })
        .limit(100);
      setGymMembers((data ?? []).map((p: any) => ({
        id: p.id,
        handle: p.handle ?? '',
        display_name: p.display_name ?? '',
      })));
    } catch (e) {
      console.warn('Failed to load gym members:', e);
      setGymMembers([]);
    }
    setLoadingGym(false);
  }, [gymId]);

  useEffect(() => {
    if (expanded) loadGymMembers();
  }, [expanded, loadGymMembers]);

  const filteredMembers = gymMembers.filter((m) => {
    if (!gymSearch.trim()) return true;
    const q = gymSearch.trim().toLowerCase();
    return m.handle.toLowerCase().includes(q) || m.display_name.toLowerCase().includes(q);
  });

  const ensureTrainerProfile = async () => {
    try {
      const existing = await getMyTrainerProfile();
      if (!existing) {
        await createTrainerProfile();
      }
    } catch { /* trainer_profiles table may not exist */ }
  };

  const handleAddClient = async (username: string) => {
    setLookupLoading(true);
    try {
      await ensureTrainerProfile();
      await requestTrainerClientLinkByUsername(username);
      const msg = `Request sent to @${username}. They'll need to accept in their app.`;
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Request Sent', msg);
      setLookupUsername('');
      setExpanded(false);
      onClientAdded();
    } catch (e: any) {
      const msg = e.message === 'cannot_request_self'
        ? 'You cannot add yourself as a client.'
        : e.message === 'User not found'
          ? 'No user found with that username.'
          : `Unable to add client: ${e.message || 'Unknown error'}`;
      Platform.OS === 'web' ? alert(msg) : Alert.alert('Error', msg);
    }
    setLookupLoading(false);
  };

  const handleShareInvite = async () => {
    const message = 'Join me on DUEL! Download the app and connect with me as your coach. https://douevenlift.com';
    try {
      if (Platform.OS === 'web') {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(message);
          alert('Invite link copied to clipboard!');
        }
      } else {
        await Share.share({ message });
      }
    } catch { /* user cancelled */ }
  };

  if (!expanded) {
    return (
      <View style={ac.wrapper}>
        <Text style={[ci.sectionHeader, { color: c.textTertiary }]}>Add Client</Text>
        <View style={ac.buttonRow}>
          <TouchableOpacity
            style={[ac.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={() => setExpanded(true)}>
            <UserPlus size={18} color={c.accent} />
            <Text style={[ac.actionText, { color: c.text }]}>Add by Username</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[ac.actionBtn, { backgroundColor: c.surface, borderColor: c.border }]}
            onPress={handleShareInvite}>
            <Link2 size={18} color={c.accent} />
            <Text style={[ac.actionText, { color: c.text }]}>Share Invite Link</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={ac.wrapper}>
      <Text style={[ci.sectionHeader, { color: c.textTertiary }]}>Add Client</Text>

      {/* Username entry */}
      <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
        <Text style={[ci.cardTitle, { color: c.text }]}>Enter Username</Text>
        <View style={ac.lookupRow}>
          <TextInput
            style={[cl.searchInput, { backgroundColor: c.background, borderColor: c.border, color: c.text, flex: 1, marginBottom: 0 }]}
            placeholder="@username"
            placeholderTextColor={c.textTertiary}
            value={lookupUsername}
            onChangeText={setLookupUsername}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <TouchableOpacity
            style={[ac.sendBtn, { backgroundColor: c.accent, opacity: lookupLoading || !lookupUsername.trim() ? 0.5 : 1 }]}
            disabled={lookupLoading || !lookupUsername.trim()}
            onPress={() => handleAddClient(lookupUsername.trim().replace(/^@/, ''))}>
            <UserPlus size={16} color={c.text} />
            <Text style={[ac.sendText, { color: c.text }]}>Add</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Gym member browser */}
      {gymId && (
        <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[ci.cardTitle, { color: c.text }]}>Gym Members</Text>
          <TextInput
            style={[cl.searchInput, { backgroundColor: c.background, borderColor: c.border, color: c.text }]}
            placeholder="Search gym members…"
            placeholderTextColor={c.textTertiary}
            value={gymSearch}
            onChangeText={setGymSearch}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {loadingGym ? (
            <Text style={[ci.value, { color: c.textSecondary }]}>Loading…</Text>
          ) : filteredMembers.length === 0 ? (
            <Text style={[ci.value, { color: c.textSecondary }]}>No members found.</Text>
          ) : (
            filteredMembers.slice(0, 20).map((m) => (
              <TouchableOpacity
                key={m.id}
                style={[ac.memberRow, { borderColor: c.border }]}
                onPress={() => handleAddClient(m.handle)}
                disabled={lookupLoading}>
                <View>
                  <Text style={[cl.clientName, { color: c.text }]}>{m.display_name || m.handle}</Text>
                  <Text style={[cl.clientHandle, { color: c.textSecondary }]}>@{m.handle}</Text>
                </View>
                <UserPlus size={16} color={c.accent} />
              </TouchableOpacity>
            ))
          )}
        </View>
      )}

      <TouchableOpacity onPress={() => setExpanded(false)} style={{ alignItems: 'center', marginTop: 4 }}>
        <Text style={[ac.cancelText, { color: c.textTertiary }]}>Cancel</Text>
      </TouchableOpacity>
    </View>
  );
}

// ── Pending Requests Section ──────────────────────────────────

function PendingRequests({ onAction }: { onAction: () => void }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [requests, setRequests] = useState<TrainerClientLink[]>([]);
  const [profiles, setProfiles] = useState<Map<string, { handle: string; display_name: string }>>(new Map());
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const pending = await listPendingClientRequests();
      setRequests(pending);
      if (pending.length > 0) {
        const ids = pending.map((r) => r.client_profile_id);
        try {
          const { data } = await supabase
            .from('profiles')
            .select('id, handle, display_name')
            .in('id', ids);
          const map = new Map<string, { handle: string; display_name: string }>();
          (data ?? []).forEach((p: any) => map.set(p.id, { handle: p.handle ?? '', display_name: p.display_name ?? '' }));
          setProfiles(map);
        } catch { /* */ }
      }
    })();
  }, []);

  if (requests.length === 0) return null;

  return (
    <View style={ac.wrapper}>
      <Text style={[ci.sectionHeader, { color: c.textTertiary }]}>Pending Requests</Text>
      {requests.map((req) => {
        const pf = profiles.get(req.client_profile_id);
        return (
          <View key={req.id} style={[ac.memberRow, { borderColor: c.border, backgroundColor: c.surface }]}>
            <View>
              <Text style={[cl.clientName, { color: c.text }]}>{pf?.display_name || 'Client'}</Text>
              {pf?.handle ? <Text style={[cl.clientHandle, { color: c.textSecondary }]}>@{pf.handle}</Text> : null}
            </View>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                disabled={processing === req.id}
                onPress={async () => {
                  setProcessing(req.id);
                  await acceptClientRequest(req.id, req.client_profile_id);
                  setRequests((prev) => prev.filter((r) => r.id !== req.id));
                  setProcessing(null);
                  onAction();
                }}>
                <CheckCircle size={24} color="#22c55e" />
              </TouchableOpacity>
              <TouchableOpacity
                disabled={processing === req.id}
                onPress={async () => {
                  setProcessing(req.id);
                  await rejectClientRequest(req.id, req.client_profile_id);
                  setRequests((prev) => prev.filter((r) => r.id !== req.id));
                  setProcessing(null);
                }}>
                <XCircle size={24} color="#ef4444" />
              </TouchableOpacity>
            </View>
          </View>
        );
      })}
    </View>
  );
}

// ── Full Calendar Screen (inline) ─────────────────────────────

type CalView = 'week' | 'month';

function FullCalendar({
  clients,
  onClose,
}: {
  clients: ClientRow[];
  onClose: () => void;
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const [calView, setCalView] = useState<CalView>('week');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [newClientName, setNewClientName] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newRecur, setNewRecur] = useState<RecurType>('none');
  const [newSessionType, setNewSessionType] = useState<SessionType>('gym');
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    loadSchedule().then(setEntries);
  }, []);

  const dayEntries = entries
    .filter((e) => e.date === selectedDate)
    .sort((a, b) => a.time.localeCompare(b.time));

  const addEntry = async () => {
    const clientName = selectedClientId
      ? (clients.find((cl) => cl.clientProfileId === selectedClientId)?.displayName ?? newClientName.trim())
      : newClientName.trim();
    if (!newTime.trim() || !clientName) return;

    const recurGroupId = newRecur !== 'none'
      ? `recur-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      : undefined;

    const newEntries: ScheduleEntry[] = [];
    const weeks = newRecur === 'weekly' ? 12 : newRecur === 'biweekly' ? 6 : 1;
    const interval = newRecur === 'biweekly' ? 14 : 7;

    for (let i = 0; i < weeks; i++) {
      const d = new Date(selectedDate + 'T12:00:00');
      if (i > 0) d.setDate(d.getDate() + interval * i);
      newEntries.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        clientName,
        clientId: selectedClientId || '',
        time: newTime.trim(),
        notes: newNotes.trim(),
        date: d.toISOString().split('T')[0],
        recur: i === 0 ? newRecur : undefined,
        recurGroupId,
        sessionType: newSessionType,
      });
    }

    const updated = [...entries, ...newEntries];
    setEntries(updated);
    await saveSchedule(updated);
    setNewTime('');
    setNewClientName('');
    setSelectedClientId('');
    setNewNotes('');
    setNewRecur('none');
    setNewSessionType('gym');
  };

  const removeEntry = async (id: string, removeAll?: boolean) => {
    const entry = entries.find((e) => e.id === id);
    let updated: ScheduleEntry[];
    if (removeAll && entry?.recurGroupId) {
      updated = entries.filter((e) => e.recurGroupId !== entry.recurGroupId);
    } else {
      updated = entries.filter((e) => e.id !== id);
    }
    setEntries(updated);
    await saveSchedule(updated);
  };

  // Generate week days around selected date
  const getWeekDays = () => {
    const base = new Date(selectedDate + 'T12:00:00');
    const dayOfWeek = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - ((dayOfWeek + 6) % 7));
    const days: string[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push(d.toISOString().split('T')[0]);
    }
    return days;
  };

  // Generate month grid
  const getMonthDays = () => {
    const base = new Date(selectedDate + 'T12:00:00');
    const year = base.getFullYear();
    const month = base.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Mon=0
    const days: (string | null)[] = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayStr = new Date().toISOString().split('T')[0];

  const shiftWeek = (dir: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const shiftMonth = (dir: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setMonth(d.getMonth() + dir);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const RECUR_OPTS: { key: RecurType; label: string }[] = [
    { key: 'none', label: 'One-time' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'biweekly', label: 'Biweekly' },
  ];

  return (
    <ScrollView style={[styles.container, { backgroundColor: c.background }]}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onClose}>
          <Text style={[{ color: c.accent, fontSize: 15, fontWeight: '600' }]}>← Back</Text>
        </TouchableOpacity>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
          <Text style={[styles.title, { color: c.text }]}>Schedule</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity
              style={[cal.viewToggle, calView === 'week' && { backgroundColor: c.accent + '22', borderColor: c.accent }]}
              onPress={() => setCalView('week')}>
              <Text style={[cal.viewToggleText, { color: calView === 'week' ? c.accent : c.textSecondary }]}>Week</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[cal.viewToggle, calView === 'month' && { backgroundColor: c.accent + '22', borderColor: c.accent }]}
              onPress={() => setCalView('month')}>
              <Text style={[cal.viewToggleText, { color: calView === 'month' ? c.accent : c.textSecondary }]}>Month</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>

      {calView === 'week' ? (
        /* Week selector */
        <View style={cal.weekRow}>
          <TouchableOpacity onPress={() => shiftWeek(-1)}>
            <Text style={[cal.navArrow, { color: c.accent }]}>‹</Text>
          </TouchableOpacity>
          {weekDays.map((day, i) => {
            const isSelected = day === selectedDate;
            const isToday = day === todayStr;
            const hasEntries = entries.some((e) => e.date === day);
            return (
              <TouchableOpacity
                key={day}
                style={[cal.dayCell, isSelected && { backgroundColor: c.accent, borderRadius: 2 }]}
                onPress={() => setSelectedDate(day)}>
                <Text style={[cal.dayLabel, { color: isSelected ? c.text : c.textSecondary }]}>
                  {DAY_LABELS[i]}
                </Text>
                <Text style={[cal.dayNum, { color: isSelected ? c.text : isToday ? c.accent : c.textSecondary, fontWeight: isToday ? '900' : '700' }]}>
                  {new Date(day + 'T12:00:00').getDate()}
                </Text>
                {hasEntries && !isSelected && <View style={[cal.dot, { backgroundColor: c.accent }]} />}
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity onPress={() => shiftWeek(1)}>
            <Text style={[cal.navArrow, { color: c.accent }]}>›</Text>
          </TouchableOpacity>
        </View>
      ) : (
        /* Month view */
        <View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <TouchableOpacity onPress={() => shiftMonth(-1)}>
              <Text style={[cal.navArrow, { color: c.accent }]}>‹</Text>
            </TouchableOpacity>
            <Text style={[{ color: c.text, fontSize: 16, fontWeight: '700' }]}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </Text>
            <TouchableOpacity onPress={() => shiftMonth(1)}>
              <Text style={[cal.navArrow, { color: c.accent }]}>›</Text>
            </TouchableOpacity>
          </View>
          <View style={{ flexDirection: 'row', marginBottom: 4 }}>
            {DAY_LABELS.map((lbl) => (
              <View key={lbl} style={{ flex: 1, alignItems: 'center' }}>
                <Text style={{ color: c.textSecondary, fontSize: 11, fontWeight: '600' }}>{lbl}</Text>
              </View>
            ))}
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {getMonthDays().map((day, i) => {
              if (!day) return <View key={`pad-${i}`} style={{ width: '14.28%', height: 40 }} />;
              const isSelected = day === selectedDate;
              const isToday = day === todayStr;
              const hasEntries = entries.some((e) => e.date === day);
              return (
                <TouchableOpacity
                  key={day}
                  style={{ width: '14.28%', height: 40, alignItems: 'center', justifyContent: 'center' }}
                  onPress={() => setSelectedDate(day)}>
                  <View style={[
                    { width: 32, height: 32, borderRadius: 2, alignItems: 'center', justifyContent: 'center' },
                    isSelected && { backgroundColor: c.accent },
                  ]}>
                    <Text style={{ color: isSelected ? c.text : isToday ? c.accent : c.textSecondary, fontSize: 14, fontWeight: isToday || isSelected ? '800' : '500' }}>
                      {new Date(day + 'T12:00:00').getDate()}
                    </Text>
                  </View>
                  {hasEntries && !isSelected && <View style={[cal.dot, { backgroundColor: c.accent, marginTop: -2 }]} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      )}

      {/* Day entries */}
      <Text style={[ci.sectionHeader, { color: c.textTertiary, marginTop: 16 }]}>
        {selectedDate === todayStr ? 'Today' : new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
      </Text>

      {dayEntries.length === 0 ? (
        <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border }]}>
          <Text style={[ci.value, { color: c.textSecondary }]}>No sessions scheduled.</Text>
        </View>
      ) : (
        dayEntries.map((entry) => (
          <View key={entry.id} style={[sched.entryCard, { backgroundColor: c.surface, borderColor: c.border }]}>
            <View style={sched.entryTime}>
              <Clock size={14} color={c.textTertiary} />
              <Text style={[sched.timeText, { color: c.text }]}>{entry.time}</Text>
            </View>
            <View style={[sched.entryInfo, { flex: 1 }]}>
              <Text style={[sched.clientNameText, { color: c.text }]}>{entry.clientName}</Text>
              {entry.sessionType && entry.sessionType !== 'gym' && (
                <Text style={{ color: c.accent, fontSize: 11, fontWeight: '600', marginTop: 1 }}>
                  {SESSION_TYPES.find((t) => t.key === entry.sessionType)?.label ?? ''}
                </Text>
              )}
              {entry.notes ? <Text style={[sched.notesText, { color: c.textSecondary }]} numberOfLines={1}>{entry.notes}</Text> : null}
              {entry.recurGroupId && <Text style={{ color: c.textTertiary, fontSize: 10, marginTop: 2 }}>Recurring</Text>}
            </View>
            <TouchableOpacity
              onPress={() => {
                if (entry.recurGroupId) {
                  if (Platform.OS === 'web') {
                    const all = confirm('Delete all recurring sessions? Cancel to delete just this one.');
                    removeEntry(entry.id, all);
                  } else {
                    Alert.alert('Delete Session', 'Delete just this session or all recurring?', [
                      { text: 'Cancel', style: 'cancel' },
                      { text: 'This Only', onPress: () => removeEntry(entry.id, false) },
                      { text: 'All Recurring', style: 'destructive', onPress: () => removeEntry(entry.id, true) },
                    ]);
                  }
                } else {
                  removeEntry(entry.id);
                }
              }}>
              <XCircle size={18} color={c.textTertiary} />
            </TouchableOpacity>
          </View>
        ))
      )}

      {/* Add entry */}
      <View style={[ci.card, { backgroundColor: c.surface, borderColor: c.border, marginTop: 12 }]}>
        <Text style={[ci.cardTitle, { color: c.text }]}>Add Session</Text>

        {/* Client: pick from list or type name */}
        {clients.length > 0 ? (
          <View style={{ gap: 4, marginBottom: 8 }}>
            {clients.map((client) => (
              <TouchableOpacity
                key={client.clientProfileId}
                style={[
                  ac.memberRow,
                  {
                    borderColor: selectedClientId === client.clientProfileId ? c.accent : c.border,
                    backgroundColor: selectedClientId === client.clientProfileId ? (c.accent + '15') : 'transparent',
                  },
                ]}
                onPress={() => {
                  setSelectedClientId(client.clientProfileId);
                  setNewClientName(client.displayName);
                }}>
                <Text style={[cl.clientName, { color: c.text }]}>{client.displayName}</Text>
                {selectedClientId === client.clientProfileId && <CheckCircle size={16} color={c.accent} />}
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <TextInput
            style={[cl.searchInput, { backgroundColor: c.background, borderColor: c.border, color: c.text }]}
            placeholder="Client name"
            placeholderTextColor={c.textTertiary}
            value={newClientName}
            onChangeText={setNewClientName}
          />
        )}

        <TextInput
          style={[cl.searchInput, { backgroundColor: c.background, borderColor: c.border, color: c.text }]}
          placeholder="Time (e.g. 9:00 AM)"
          placeholderTextColor={c.textTertiary}
          value={newTime}
          onChangeText={setNewTime}
        />

        <TextInput
          style={[cl.searchInput, { backgroundColor: c.background, borderColor: c.border, color: c.text }]}
          placeholder="Notes (optional)"
          placeholderTextColor={c.textTertiary}
          value={newNotes}
          onChangeText={setNewNotes}
        />

        {/* Session type */}
        <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600', marginBottom: 4 }}>Session Type</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {SESSION_TYPES.map((opt) => (
            <TouchableOpacity
              key={opt.key}
              style={[
                cal.viewToggle,
                newSessionType === opt.key && { backgroundColor: c.accent + '22', borderColor: c.accent },
              ]}
              onPress={() => setNewSessionType(opt.key)}>
              <Text style={[cal.viewToggleText, { color: newSessionType === opt.key ? c.accent : c.textSecondary }]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Recurring options */}
        <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600', marginBottom: 4 }}>Repeat</Text>
        <View style={{ flexDirection: 'row', gap: 6, marginBottom: 12 }}>
          {RECUR_OPTS.map((opt) => (
            <TouchableOpacity
              key={opt.key}
              style={[
                cal.viewToggle,
                newRecur === opt.key && { backgroundColor: c.accent + '22', borderColor: c.accent },
              ]}
              onPress={() => setNewRecur(opt.key)}>
              <Text style={[cal.viewToggleText, { color: newRecur === opt.key ? c.accent : c.textSecondary }]}>
                {opt.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={[ac.sendBtn, {
            backgroundColor: c.accent,
            opacity: !newTime.trim() || (!selectedClientId && !newClientName.trim()) ? 0.5 : 1,
            alignSelf: 'flex-end',
          }]}
          disabled={!newTime.trim() || (!selectedClientId && !newClientName.trim())}
          onPress={addEntry}>
          <Text style={[ac.sendText, { color: c.text }]}>Add</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

// ── Main Screen ───────────────────────────────────────────────

export default function TrainerDashboardScreen() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [loading, setLoading] = useState(true);
  const [insightsData, setInsightsData] = useState<CoachInsightsData | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [search, setSearch] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);

    try {
      const links = await listMyClientLinks();
      const activeLinks = links.filter((l: TrainerClientLink) => l.status === 'active');
      const activeClientIds = activeLinks.map((l: TrainerClientLink) => l.client_profile_id);

      let clientRows: ClientRow[] = [];
      if (activeClientIds.length > 0) {
        try {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, handle, display_name')
            .in('id', activeClientIds);

          const profileMap = new Map<string, { handle: string | null; display_name: string | null }>();
          (profiles ?? []).forEach((p: any) => {
            profileMap.set(p.id, { handle: p.handle ?? null, display_name: p.display_name ?? null });
          });

          clientRows = activeLinks.map((link) => {
            const pf = profileMap.get(link.client_profile_id);
            return {
              linkId: link.id,
              clientProfileId: link.client_profile_id,
              displayName: pf?.display_name ?? 'Client',
              handle: pf?.handle ?? null,
            };
          });
        } catch (e) {
          console.warn('Failed to load client profiles:', e);
          clientRows = activeLinks.map((link) => ({
            linkId: link.id,
            clientProfileId: link.client_profile_id,
            displayName: 'Client',
            handle: null,
          }));
        }
      }

      setClients(clientRows);

      let activity7d: number | null = null;
      let activity30d: number | null = null;
      if (activeClientIds.length > 0) {
        try {
          const now = new Date();
          const d7 = new Date(now); d7.setDate(d7.getDate() - 7);
          const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
          const { count: c7 } = await supabase.from('workouts').select('id', { count: 'exact', head: true }).in('user_id', activeClientIds).gte('performed_at', d7.toISOString());
          activity7d = c7 ?? 0;
          const { count: c30 } = await supabase.from('workouts').select('id', { count: 'exact', head: true }).in('user_id', activeClientIds).gte('performed_at', d30.toISOString());
          activity30d = c30 ?? 0;
        } catch { /* */ }
      }

      setInsightsData({
        activeClients: activeClientIds.length, totalClients: links.length,
        activity7d, activity30d,
      });
    } catch (e) {
      console.warn('Failed to load insights data:', e);
      setInsightsData(null);
    }

    setLoading(false);
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  if (showCalendar) {
    return (
      <PaywallGate requiredTier="coach_pro">
        <FullCalendar clients={clients} onClose={() => setShowCalendar(false)} />
      </PaywallGate>
    );
  }

  return (
    <PaywallGate requiredTier="coach_pro">
      <ScrollView style={[styles.container, { backgroundColor: c.background }]}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: c.text }]}>Trainer Dashboard</Text>
          {!loading && insightsData && (
            <Text style={[styles.subtitle, { color: c.textSecondary }]}>
              {insightsData.activeClients} active client{insightsData.activeClients !== 1 ? 's' : ''}
            </Text>
          )}
        </View>

        {loading ? (
          <SkeletonRow rows={4} />
        ) : (
          <>
            <TodaySchedule clients={clients} onViewFull={() => setShowCalendar(true)} />
            <CoachInsights data={insightsData} />
            <PendingRequests onAction={load} />
            <AddClientSection onClientAdded={load} gymId={(profile as any)?.gym_id ?? null} />
            <ClientList clients={clients} search={search} onSearch={setSearch} />
          </>
        )}
      </ScrollView>
    </PaywallGate>
  );
}

// ── Styles ────────────────────────────────────────────────────

const ci = StyleSheet.create({
  wrapper: { marginBottom: 20 },
  sectionHeader: {
    fontSize: 13, fontWeight: '700', letterSpacing: 0.5,
    textTransform: 'uppercase', marginBottom: 10,
  },
  loadingSection: {
    borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 16,
    alignItems: 'center', justifyContent: 'center', minHeight: 60,
  },
  card: { borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 10 },
  cardTitle: { fontSize: 14, fontWeight: '700', marginBottom: 6 },
  value: { fontSize: 15, fontWeight: '600' },
  row: {
    flexDirection: 'row', justifyContent: 'space-between',
    alignItems: 'center', paddingVertical: 4,
  },
  label: { fontSize: 13, fontWeight: '500' },
  rowValue: { fontSize: 13, fontWeight: '600' },
  disclaimer: {
    fontSize: 11, fontStyle: 'italic', textAlign: 'center', marginTop: 4,
  },
});

const cl = StyleSheet.create({
  wrapper: { marginBottom: 20 },
  searchInput: {
    borderWidth: 1, borderRadius: 2, padding: 12,
    fontSize: 14, marginBottom: 12,
  },
  clientCard: {
    borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 8,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
  clientName: { fontSize: 15, fontWeight: '600' },
  clientHandle: { fontSize: 13, marginTop: 2 },
  chevron: { fontSize: 22, fontWeight: '300' },
});

const sched = StyleSheet.create({
  wrapper: { marginBottom: 20 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  viewAllBtn: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  viewAllText: { fontSize: 13, fontWeight: '600' },
  entryCard: {
    borderWidth: 1, borderRadius: 2, padding: 12, marginBottom: 6,
    flexDirection: 'row', alignItems: 'center', gap: 12,
  },
  entryTime: { flexDirection: 'row', alignItems: 'center', gap: 4, minWidth: 80 },
  timeText: { fontSize: 14, fontWeight: '700' },
  entryInfo: {},
  clientNameText: { fontSize: 14, fontWeight: '600' },
  notesText: { fontSize: 12, marginTop: 2 },
});

const ac = StyleSheet.create({
  wrapper: { marginBottom: 20 },
  buttonRow: { flexDirection: 'row', gap: 8 },
  actionBtn: {
    flex: 1, borderWidth: 1, borderRadius: 2, padding: 14,
    alignItems: 'center', gap: 8,
  },
  actionText: { fontSize: 13, fontWeight: '600' },
  lookupRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  sendBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingVertical: 10, paddingHorizontal: 16, borderRadius: 2,
  },
  sendText: { fontSize: 13, fontWeight: '700' },
  cancelText: { fontSize: 13, fontWeight: '600', padding: 8 },
  memberRow: {
    borderWidth: 1, borderRadius: 2, padding: 10,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
  },
});

const cal = StyleSheet.create({
  weekRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  navArrow: { fontSize: 28, fontWeight: '300', paddingHorizontal: 4 },
  dayCell: { alignItems: 'center', paddingVertical: 6, paddingHorizontal: 8 },
  dayLabel: { fontSize: 11, fontWeight: '600' },
  dayNum: { fontSize: 16, fontWeight: '700', marginTop: 2 },
  dot: { width: 4, height: 4, borderRadius: 2, marginTop: 2 },
  viewToggle: {
    paddingVertical: 5, paddingHorizontal: 12,
    borderRadius: 2, borderWidth: 1, borderColor: 'transparent',
  },
  viewToggleText: { fontSize: 12, fontWeight: '700' },
});

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20 },
  header: { marginBottom: 20 },
  title: { fontSize: 24, fontWeight: '700' },
  subtitle: { fontSize: 14, marginTop: 4 },
});
